import React, { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HubHome } from './platform/ui/HubHome';
import { PortalLayout } from './platform/ui/PortalLayout';
import { GamePage } from './platform/ui/GamePage';
import { AboutPage, ContactPage, FAQPage, PrivacyPage, TermsOfServicePage, CategoriesPage, BlogsPage, NotFoundPage } from './platform/pages/InfoPages';
import { WikiPage } from './platform/pages/WikiPage';
import './platform/ui/game-theme.css';
import { SharedGamePopup } from './platform/ui/SharedGamePopup';
const FindTheNumberGame = lazy(() => import('./games/find-the-number/GameContainer').then(m => ({ default: m.FindTheNumberGame })));
const FindTheNumberUI = lazy(() => import('./games/find-the-number/ui/GameUI').then(m => ({ default: m.GameUI })));
const FindTheNumberLevelSelect = lazy(() => import('./games/find-the-number/ui/LevelSelect').then(m => ({ default: m.LevelSelect })));
import { useGameStore as useFindTheNumberStore } from './games/find-the-number/store/useGameStore';

const BallSortGame = lazy(() => import('./games/ball-sort/GameContainer').then(m => ({ default: m.BallSortGame })));
const BallSortUI = lazy(() => import('./games/ball-sort/ui/GameUI').then(m => ({ default: m.GameUI })));

const CryptogramGame = lazy(() => import('./games/cryptogram-quotes/GameContainer').then(m => ({ default: m.CryptogramGame })));
const CryptogramUI = lazy(() => import('./games/cryptogram-quotes/ui/GameUI').then(m => ({ default: m.GameUI })));

const LudoGame = lazy(() => import('./games/ludo-royale/GameContainer').then(m => ({ default: m.LudoGame })));
const LudoUI = lazy(() => import('./games/ludo-royale/ui/GameUI').then(m => ({ default: m.GameUI })));

const BubbleGame = lazy(() => import('./games/bubble-shooter/GameContainer').then(m => ({ default: m.GameContainer })));
const BubbleUI = lazy(() => import('./games/bubble-shooter/ui/GameUI').then(m => ({ default: m.GameUI })));

const SudokuGame = lazy(() => import('./games/sudoku/GameContainer').then(m => ({ default: m.GameContainer })));
const SudokuUI = lazy(() => import('./games/sudoku/ui/GameUI').then(m => ({ default: m.GameUI })));

const ChessGame = lazy(() => import('./games/chess/GameContainer').then(m => ({ default: m.GameContainer })));
const ChessUI = lazy(() => import('./games/chess/ui/GameUI').then(m => ({ default: m.GameUI })));

const BoggleGame = lazy(() => import('./games/boggle/GameContainer').then(m => ({ default: m.GameContainer })));
const BoggleUI = lazy(() => import('./games/boggle/ui/GameUI').then(m => ({ default: m.GameUI })));

const DotsGame = lazy(() => import('./games/dots-connect/GameContainer').then(m => ({ default: m.GameContainer })));
const DotsUI = lazy(() => import('./games/dots-connect/ui/GameUI').then(m => ({ default: m.GameUI })));

const EmojiConnectGame = lazy(() => import('./games/emoji-connect/GameContainer').then(m => ({ default: m.GameContainer })));
const EmojiConnectUI = lazy(() => import('./games/emoji-connect/ui/GameUI').then(m => ({ default: m.GameUI })));

const ArrowEscapeGame = lazy(() => import('./games/arrow-escape/GameContainer').then(m => ({ default: m.GameContainer })));
const ArrowEscapeUI = lazy(() => import('./games/arrow-escape/ui/GameUI').then(m => ({ default: m.GameUI })));

const FourInARowGame = lazy(() => import('./games/four-in-a-row/GameContainer').then(m => ({ default: m.GameContainer })));
const FourInARowUI = lazy(() => import('./games/four-in-a-row/ui/GameUI').then(m => ({ default: m.GameUI })));

function EmojiConnectWrapper() {
  return (
    <>
      <Helmet>
        <title>Emoji Connect | Gamezolve</title>
        <meta name="description" content="Connect matching emojis before you run out of moves!" />
      </Helmet>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <EmojiConnectGame />
        <EmojiConnectUI />
      </div>
    </>
  );
}

function FindTheNumberWrapper() {
  const ftnUiState = useFindTheNumberStore((state) => state.uiState);
  return (
    <>
      <Helmet>
        <title>Find The Number | Gamezolve</title>
        <meta name="description" content="A relaxing, paper-styled puzzle game where you scan and find hidden numbers." />
      </Helmet>
      {ftnUiState === 'level_select' && <FindTheNumberLevelSelect />}
      {(ftnUiState === 'playing' || ftnUiState === 'complete') && (
        <>
          <FindTheNumberGame />
          <FindTheNumberUI />
        </>
      )}
    </>
  );
}

function BallSortWrapper() {
  return (
    <>
      <Helmet>
        <title>Ball Sort | Gamezolve</title>
        <meta name="description" content="A premium color-sorting puzzle with beautiful glass tubes and perfect solvability." />
      </Helmet>
      <BallSortGame />
      <BallSortUI />
    </>
  );
}

function CryptogramWrapper() {
  return (
    <>
      <Helmet>
        <title>Cryptogram Quotes | Gamezolve</title>
        <meta name="description" content="A premium quote-decoding puzzle game with smart focus and beautiful typography." />
      </Helmet>
      <CryptogramGame />
      <CryptogramUI />
    </>
  );
}

function LudoWrapper() {
  return (
    <>
      <Helmet>
        <title>Ludo Royale | Gamezolve</title>
        <meta name="description" content="A premium Ludo board game with bot AI and smooth animations." />
      </Helmet>
      <LudoGame />
      <LudoUI />
    </>
  );
}

function BubbleWrapper() {
  return (
    <>
      <Helmet>
        <title>Bubble Shooter Royale | Gamezolve</title>
        <meta name="description" content="A premium arcade bubble shooter with tiered endurance gameplay." />
      </Helmet>
      <BubbleGame />
      <BubbleUI />
    </>
  );
}

function SudokuWrapper() {
  return (
    <>
      <Helmet>
        <title>Sudoku | Gamezolve</title>
        <meta name="description" content="A premium Sudoku experience with multiple difficulties and modes." />
      </Helmet>
      <SudokuGame />
      <SudokuUI />
    </>
  );
}

function ChessWrapper() {
  return (
    <>
      <Helmet>
        <title>Chess Royale | Gamezolve</title>
        <meta name="description" content="The classic game of kings, built with a premium glassmorphic design and AI support." />
      </Helmet>
      <ChessGame />
      <ChessUI />
    </>
  );
}

const WaterSortGame = lazy(() => import('./games/water-sort/GameContainer').then(m => ({ default: m.WaterSortGame })));
const WaterSortUI = lazy(() => import('./games/water-sort/ui/GameUI').then(m => ({ default: m.GameUI })));

function WaterSortWrapper() {
  return (
    <>
      <Helmet>
        <title>Water Sort | Gamezolve</title>
        <meta name="description" content="A premium liquid-sorting puzzle with beautiful pouring animations." />
      </Helmet>
      <WaterSortGame />
      <WaterSortUI />
    </>
  );
}

function BoggleWrapper() {
  return (
    <>
      <Helmet>
        <title>Boggle | Gamezolve</title>
        <meta name="description" content="A premium mobile-first word tracing game." />
      </Helmet>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <BoggleGame />
        <BoggleUI />
      </div>
    </>
  );
}

function DotsWrapper() {
  return (
    <>
      <Helmet>
        <title>Dots | Gamezolve</title>
        <meta name="description" content="Connect matching colors before you run out of moves!" />
      </Helmet>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <DotsGame />
        <DotsUI />
      </div>
    </>
  );
}

const ColorSwitchGame = lazy(() => import('./games/color-switch/GameContainer').then(m => ({ default: m.GameContainer })));
const ColorSwitchUI = lazy(() => import('./games/color-switch/ui/GameUI').then(m => ({ default: m.GameUI })));

function ColorSwitchWrapper() {
  return (
    <>
      <Helmet>
        <title>Color Switch | Gamezolve</title>
        <meta name="description" content="A premium, neon arcade game of endless color matching reflexes." />
      </Helmet>
      <ColorSwitchGame />
      <ColorSwitchUI />
    </>
  );
}

const Game2048 = lazy(() => import('./games/2048/GameContainer').then(m => ({ default: m.GameContainer })));
const Game2048UI = lazy(() => import('./games/2048/ui/GameUI').then(m => ({ default: m.GameUI })));

function Game2048Wrapper() {
  return (
    <>
      <Helmet>
        <title>2048 | Gamezolve</title>
        <meta name="description" content="The classic 2048 puzzle game with smooth sliding animations." />
      </Helmet>
      <Game2048 />
      <Game2048UI />
    </>
  );
}

const ColorMatchGame = lazy(() => import('./games/color-match/GameContainer').then(m => ({ default: m.GameContainer })));
const ColorMatchUI = lazy(() => import('./games/color-match/ui/GameUI').then(m => ({ default: m.GameUI })));

function ColorMatchWrapper() {
  return (
    <>
      <Helmet>
        <title>Color Match | Gamezolve</title>
        <meta name="description" content="A fun and colorful match puzzle game where you pop matching numbers." />
      </Helmet>
      <ColorMatchGame />
      <ColorMatchUI />
    </>
  );
}

function ArrowEscapeWrapper() {
  return (
    <>
      <Helmet>
        <title>Arrow Escape | Gamezolve</title>
        <meta name="description" content="A tap-away style block puzzle where you clear arrows off the grid." />
      </Helmet>
      <ArrowEscapeGame />
      <ArrowEscapeUI />
    </>
  );
}

const EmojiMatchGame = lazy(() => import('./games/emoji-match/GameContainer').then(m => ({ default: m.GameContainer })));
const EmojiMatchUI = lazy(() => import('./games/emoji-match/ui/GameUI').then(m => ({ default: m.GameUI })));

function EmojiMatchWrapper() {
  return (
    <>
      <Helmet>
        <title>Emoji Match | Gamezolve</title>
        <meta name="description" content="Connect related emojis in this fun puzzle game." />
      </Helmet>
      <EmojiMatchGame />
      <EmojiMatchUI />
    </>
  );
}

const EmojiSortGame = lazy(() => import('./games/emoji-sort/GameContainer').then(m => ({ default: m.EmojiSortGame })));
const EmojiSortUI = lazy(() => import('./games/emoji-sort/ui/GameUI').then(m => ({ default: m.GameUI })));

function EmojiSortWrapper() {
  return (
    <>
      <Helmet>
        <title>Emoji Sort | Gamezolve</title>
        <meta name="description" content="A fun emoji sorting puzzle game." />
      </Helmet>
      <EmojiSortGame />
      <EmojiSortUI />
    </>
  );
}

const NeonMazeGame = lazy(() => import('./games/neon-maze/GameContainer').then(m => ({ default: m.MazeGame })));
const NeonMazeUI = lazy(() => import('./games/neon-maze/ui/GameUI').then(m => ({ default: m.GameUI })));

function NeonMazeWrapper() {
  return (
    <>
      <Helmet>
        <title>Neon Maze | Gamezolve</title>
        <meta name="description" content="A modern neon sliding maze game." />
      </Helmet>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <NeonMazeGame />
        <NeonMazeUI />
      </div>
    </>
  );
}

const FlagGuessGame = lazy(() => import('./games/flag-guess/GameContainer').then(m => ({ default: m.GameContainer })));

function FlagGuessWrapper() {
  return (
    <>
      <Helmet>
        <title>Flag Guesser | Gamezolve</title>
        <meta name="description" content="Test your geography knowledge and guess the flags of the world!" />
      </Helmet>
      <FlagGuessGame />
    </>
  );
}

const EmojiDropMergeGame = lazy(() => import('./games/emoji-drop-merge/GameContainer').then(m => ({ default: m.GameContainer })));
const EmojiDropMergeUI = lazy(() => import('./games/emoji-drop-merge/ui/GameUI').then(m => ({ default: m.GameUI })));

function EmojiDropMergeWrapper() {
  return (
    <>
      <Helmet>
        <title>Emoji Drop Merge | Gamezolve</title>
        <meta name="description" content="A satisfying endless emoji merging physics puzzle!" />
      </Helmet>
      <EmojiDropMergeGame />
      <EmojiDropMergeUI />
    </>
  );
}

const RestaurantMergeRushGame = lazy(() => import('./games/restaurant-merge-rush/GameContainer').then(m => ({ default: m.GameContainer })));

function RestaurantMergeRushWrapper() {
  return (
    <>
      <Helmet>
        <title>Restaurant Merge Rush | Gamezolve</title>
        <meta name="description" content="Merge ingredients, serve hungry customers, and upgrade your dream restaurant!" />
      </Helmet>
      <RestaurantMergeRushGame />
    </>
  );
}

const CatMergeGame = lazy(() => import('./games/cat-merge/GameContainer').then(m => ({ default: m.GameContainer })));

function CatMergeWrapper() {
  return (
    <>
      <Helmet>
        <title>Cat Merge | Gamezolve</title>
        <meta name="description" content="Merge adorable cats in this addictive puzzle game!" />
      </Helmet>
      <CatMergeGame />
    </>
  );
}

function FourInARowWrapper() {
  return (
    <>
      <Helmet>
        <title>Four in a Row | Gamezolve</title>
        <meta name="description" content="Play the classic Four in a Row game against AI or locally with a friend!" />
      </Helmet>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <FourInARowGame />
        <FourInARowUI />
      </div>
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div style={{ width: '100%', minHeight: '100vh', position: 'relative' }}>
      <ScrollToTop />
      <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "24px", fontWeight: "bold", color: "#6b7280" }}>Loading...</div>}>
      <Routes>
        <Route path="/" element={<PortalLayout />}>
          <Route index element={
            <>
              <Helmet>
                <title>Gamezolve | Play Free Online Games</title>
                <meta name="description" content="Play the best free online games directly in your browser on Gamezolve." />
              </Helmet>
              <HubHome />
            </>
          } />
          
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsOfServicePage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="blogs" element={<BlogsPage />} />
          <Route path="*" element={<NotFoundPage />} />

          <Route path="game/find-the-number" element={<GamePage gameId="find_the_number"><FindTheNumberWrapper /></GamePage>} />
          <Route path="game/ball-sort" element={<GamePage gameId="ball_sort"><BallSortWrapper /></GamePage>} />
          <Route path="game/water-sort" element={<GamePage gameId="water_sort"><WaterSortWrapper /></GamePage>} />
          <Route path="game/cryptogram-quotes" element={<GamePage gameId="cryptogram"><CryptogramWrapper /></GamePage>} />
          <Route path="game/ludo-royale" element={<GamePage gameId="ludo"><LudoWrapper /></GamePage>} />
          <Route path="game/bubble-shooter" element={<GamePage gameId="bubble_shooter"><BubbleWrapper /></GamePage>} />
          <Route path="game/sudoku" element={<GamePage gameId="sudoku"><SudokuWrapper /></GamePage>} />
          <Route path="game/chess" element={<GamePage gameId="chess"><ChessWrapper /></GamePage>} />
          <Route path="game/boggle" element={<GamePage gameId="boggle"><BoggleWrapper /></GamePage>} />
          <Route path="game/four-in-a-row" element={<GamePage gameId="four-in-a-row"><FourInARowWrapper /></GamePage>} />
          <Route path="game/dots-connect" element={<GamePage gameId="dots-connect"><DotsWrapper /></GamePage>} />
          <Route path="game/emoji-connect" element={<GamePage gameId="emoji-connect"><EmojiConnectWrapper /></GamePage>} />
          <Route path="game/color-switch" element={<GamePage gameId="color_switch"><ColorSwitchWrapper /></GamePage>} />
          <Route path="game/2048" element={<GamePage gameId="2048"><Game2048Wrapper /></GamePage>} />
          <Route path="game/color-match" element={<GamePage gameId="color_match"><ColorMatchWrapper /></GamePage>} />
          <Route path="game/arrow-escape" element={<GamePage gameId="arrow_escape"><ArrowEscapeWrapper /></GamePage>} />
          <Route path="game/emoji-match" element={<GamePage gameId="emoji_match"><EmojiMatchWrapper /></GamePage>} />
          <Route path="game/emoji-sort" element={<GamePage gameId="emoji_sort"><EmojiSortWrapper /></GamePage>} />
          <Route path="game/neon-maze" element={<GamePage gameId="neon_maze"><NeonMazeWrapper /></GamePage>} />
          <Route path="game/flag-guess" element={<GamePage gameId="flag-guess"><FlagGuessWrapper /></GamePage>} />
          <Route path="game/emoji-drop-merge" element={<GamePage gameId="emoji_drop_merge"><EmojiDropMergeWrapper /></GamePage>} />
          <Route path="game/restaurant-merge-rush" element={<GamePage gameId="restaurant_merge_rush"><RestaurantMergeRushWrapper /></GamePage>} />
          <Route path="game/cat-merge" element={<GamePage gameId="cat-merge"><CatMergeWrapper /></GamePage>} />
          
          <Route path="wiki/:id" element={<WikiPage />} />

          {/* Catch-all for GameMonetize Feed Games */}
          <Route path="game/:id" element={<GamePage />} />
        </Route>
      </Routes>
      </Suspense>
      <SharedGamePopup />
    </div>
  );
}

export default App;
